import type { HttpResponse } from '@develop/ia-api-library'
import { toNumber } from '@develop/ia-utils'
import type { PharmacyDetails, PharmacySwitchPharmacyIds } from '@/models/pharmacy-details'

/**
  * Checks for pharmacy mismatch, and if so, fetches the missing pharmacy details and sets them to the store, to be used later
  */
export const checkMismatchAndLoadPharmacies = async (
  { selectedPharmacyId, newPharmacyId }: PharmacySwitchPharmacyIds,
  fetchedPharmacy?: PharmacyDetails,
) => {
  onShowLogs(() => console.log('check pharmacy match', { selectedPharmacyId, newPharmacyId }))

  const mismatchById = selectedPharmacyId !== newPharmacyId

  if (mismatchById && (selectedPharmacyId || newPharmacyId)) {
    const currentPharmacyStore = useCurrentPharmacyStore()
    const currentPharmacy = currentPharmacyStore.pharmacy
    const isCurrentPharmacy = (id: number) => currentPharmacy?.id === id
    const isFetchedPharmacy = (id: number) => fetchedPharmacy?.id === id

    const promises = [selectedPharmacyId, newPharmacyId]
      .map(async (id) => {
        // reuse current pharmacy (from store)

        const pharmacyId = toNumber(id)
        if (!pharmacyId) {
          // if pharmacyId is not defined, return resolved promise -> we will set the pharmacy to null
          return Promise.resolve<HttpResponse<PharmacyDetails>>({ data: null, success: true, status: 200, message: null })
        }

        if (currentPharmacy && isCurrentPharmacy(pharmacyId))
          return Promise.resolve<HttpResponse<PharmacyDetails>>({ data: currentPharmacy, success: true, status: 200, message: null })

        // reuse already fetched pharmacy (probably from apofinder)
        if (fetchedPharmacy && isFetchedPharmacy(pharmacyId))
          return Promise.resolve<HttpResponse<PharmacyDetails>>({ data: fetchedPharmacy, success: true, status: 200, message: null })

        // fetch pharmacy details if not already fetched or stored
        const response = await fetchPharmacyDetails(pharmacyId)

        return Promise.resolve<HttpResponse<PharmacyDetails>>(response)
      })

    const [selectedPharmacyRes, newPharmacyRes] = await Promise.allSettled(promises)
    const selectedPharmacy = selectedPharmacyRes.status === 'fulfilled' ? selectedPharmacyRes.value.data : null
    const newPharmacy = newPharmacyRes.status === 'fulfilled' ? newPharmacyRes.value.data : null
    const mismatchByData = selectedPharmacy?.id !== newPharmacy?.id

    if (mismatchByData !== mismatchById) {
      onShowLogs(() => console.warn('Pharmacy mismatch by id and data do not match. Both requests probably failed.', { selectedPharmacyRes, newPharmacyRes, selectedPharmacyId, newPharmacyId }))
    }

    return {
      mismatch: mismatchByData,
      selectedPharmacy,
      newPharmacy,
    }
  }

  return {
    mismatch: mismatchById,
    selectedPharmacy: null,
    newPharmacy: null,
  }
}
